import { makeAutoObservable, runInAction } from "mobx";
import { API_URL } from "../api/apiClient";

export interface MaintenanceInfo {
  message?: string | null;
  until?: string | null;
}

/**
 * Modo mantenimiento del sistema. Cuatro fuentes de señal, en capas — cada una
 * cubre el agujero de la anterior:
 *
 * 1. Broadcast SSE `maintenance` (aviso previo con gracia): push best-effort,
 *    puede no llegar a tabs en background/PWA dormida.
 * 2. 503 con `{maintenance: true}` en cualquier response (Traefik responde
 *    incluso con el server caído): señal autoritativa, activa el overlay.
 * 3. Polling a /health mientras está activo: el "OFF" robusto — no hay server
 *    del cual recibir push durante la ventana.
 * 4. visibilitychange: re-chequeo inmediato al volver la tab al frente.
 *
 * Al recuperar se recarga la página completa: los requests en vuelo quedaron
 * abortados a mitad de camino y de paso el usuario toma el build nuevo
 * post-deploy.
 */
export class MaintenanceStore {
  status: "normal" | "warned" | "active" = "normal";
  message: string | null = null;
  until: string | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private readonly POLL_INTERVAL_MS = 15000;

  constructor(_rootStore: unknown) {
    makeAutoObservable(this);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }
  }

  /** Aviso previo (SSE): el server sigue vivo, el corte llega en `until`. */
  warn(info: MaintenanceInfo) {
    if (this.status === "active") return;
    this.status = "warned";
    this.message = info.message ?? null;
    this.until = info.until ?? null;
  }

  /** Mantenimiento efectivo (503 detectado o broadcast sin gracia). */
  activate(info: MaintenanceInfo) {
    if (this.status === "active") return;
    this.status = "active";
    this.message = info.message ?? this.message;
    this.until = info.until ?? this.until;
    this.startPolling();
  }

  /** Fin anunciado por SSE sin corte real de por medio (solo aviso). */
  clear() {
    if (this.status === "active") {
      // Hubo corte: la recuperación real la decide el polling, no un push.
      return;
    }
    this.status = "normal";
    this.message = null;
    this.until = null;
  }

  private startPolling() {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => void this.checkHealth(), this.POLL_INTERVAL_MS);
    void this.checkHealth();
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async checkHealth() {
    try {
      const response = await fetch(API_URL + "/health", { cache: "no-store" });
      if (response.ok) {
        this.recovered();
      }
    } catch {
      // Server aún caído o red cortada: seguir esperando.
    }
  }

  private recovered() {
    this.stopPolling();
    window.location.reload();
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === "visible" && this.status === "active") {
      void this.checkHealth();
    }
  };

  /** Segundos restantes hasta `until` (para el countdown del aviso). */
  get secondsRemaining(): number | null {
    if (!this.until) return null;
    const diff = Math.floor((new Date(this.until).getTime() - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  }

  dispose() {
    this.stopPolling();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
    runInAction(() => (this.status = "normal"));
  }
}
